import { c as _c } from "react/compiler-runtime";
import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { ADVISOR_BOARD, AdvisorBoardQueryData, filterEngagements } from '@src/gql/queries/AdvisorBoard';
export type { AdvisorBoardQueryData };
export { filterEngagements };
export type UseAdvisorBoardOptions = {
  skip?: boolean;
  /**
   * Default: `cache-and-network` so post-purchase / entitlement flips revalidate
   * without waiting for a hard reload. Override only when a consumer truly needs
   * another policy (e.g. network-only after a known cache write).
   */
  fetchPolicy?: WatchQueryFetchPolicy;
};

/**
 * Shared advisorBoard query for Advisors surfaces (nav, home, ask, lists, aside).
 * Always prefer this over an independent `useQuery(ADVISOR_BOARD)`.
 *
 * Freshness strategy:
 * - Default `cache-and-network` revalidates on every consumer mount / focus without
 *   requiring a hard reload (important after $299 purchase + team seat provision).
 * - Apollo Client 4 sets `loading` true while *any* network request is in flight,
 *   even when cached data is already shown — do **not** gate full-page UI on
 *   `loading` alone or lists/nav will flash a spinner on every route change.
 * - Use {@link UseAdvisorBoardResult.isInitialLoading} for blocking spinners
 *   (no data yet). Targeted post-purchase re-reads use `refreshAdvisorBoard`.
 */
export function useAdvisorBoard(options) {
  const $ = _c(6);
  const t0 = options?.fetchPolicy ?? "cache-and-network";
  const t1 = options?.skip;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      fetchPolicy: t0,
      skip: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const result = useQuery(ADVISOR_BOARD, t2);
  const isInitialLoading = !!result.loading && result.data === undefined;
  let t3;
  if ($[3] !== isInitialLoading || $[4] !== result) {
    t3 = {
      ...result,
      isInitialLoading
    };
    $[3] = isInitialLoading;
    $[4] = result;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}